import React from 'react';
import { TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField';
import Typography, { TypographyProps } from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';
import { PRESENT_PATTERN } from '../../variables';
import MomentUtils from '@material-ui/pickers/adapter/moment';
import LocalizationProvider from '@material-ui/pickers/LocalizationProvider';
import {
  DesktopTimePicker,
  DesktopTimePickerProps,
} from '@material-ui/pickers';
import Tooltip from '@material-ui/core/Tooltip';
import T from 'i18n-react';
import TextField from './TextField';
import moment from 'moment';
import { validateString } from '../PlanTerm/validate';

type InputProps = {
  textFieldProps?: Omit<MuiTextFieldProps, 'label'>,
  timeProps?: Omit<DesktopTimePickerProps, 'value'>,
  typographyProps?: TypographyProps,
  readOnly?: boolean,
};

const useStyles = makeStyles((theme) => ({
  tooltip: {
    fontSize: theme.typography.pxToRem(14),
  },
}));

export const getSize = (value) =>
  value.split('').reduce((prev, cur) => {
    if (/[a-zA-Z0-9!-/:-@¥[-`{-~]/.test(cur)) {
      return prev + 1;
    }
    // Add 2 size for full-width character
    return prev + 2;
  }, 0);

type MessageOptions = React.InputHTMLAttributes & {
  minTime?: moment.MomentInput,
  maxTime?: moment.MomentInput,
};

const getTooltipMessage = (
  type: string,
  { min, max, minLength, maxLength, minTime, maxTime }: MessageOptions,
): React.ReactNode => {
  if (type === 'time') {
    const minTimeMoment = moment(minTime);
    const maxTimeMoment = moment(maxTime);
    if (
      minTime &&
      maxTime &&
      minTimeMoment.isValid() &&
      maxTimeMoment.isValid()
    ) {
      return T.translate('textFieldTooltip.timeRangeOverflow', {
        min: minTimeMoment.format('HH:mm'),
        max: maxTimeMoment.format('HH:mm'),
      });
    } else if (minTime && minTimeMoment.isValid()) {
      return T.translate('textFieldTooltip.minTime', {
        min: minTimeMoment.format('HH:mm'),
      });
    } else if (maxTime && maxTimeMoment.isValid()) {
      return T.translate('textFieldTooltip.maxTime', {
        max: maxTimeMoment.format('HH:mm'),
      });
    }
  } else if (type === 'number') {
    if (min != null && max != null) {
      return T.translate('textFieldTooltip.numberRangeOverflow', { min, max });
    } else if (min != null) {
      return T.translate('textFieldTooltip.minNumber', { min });
    } else if (max != null) {
      return T.translate('textFieldTooltip.maxNumber', { max });
    }
  } else if (type === 'text') {
    if (maxLength != null) {
      return T.translate('textFieldTooltip.maxLengthText', { maxLength });
    }
  }

  return T.translate(`textFieldTooltip.${type || 'text'}`);
};

const getDisplayValue = (type: string, value: any): React.ReactNode => {
  switch (type) {
    case 'time':
      const time = moment(value);
      return time.isValid() ? time.format('HH:mm') : '';
    case 'number':
      return value?.toLocaleString?.();
    default:
      return value;
  }
};

const Input = ({
  textFieldProps,
  timeProps,
  typographyProps,
  readOnly,
}: InputProps) => {
  const classes = useStyles();

  const [state, setState] = React.useState({
    value: textFieldProps?.value,
    validity: textFieldProps?.error === false,
  });

  const { value, validity } = state;

  const [open, setOpen] = React.useState(false);

  if (readOnly) {
    return (
      <Typography
        variant="inherit"
        component="span"
        {...typographyProps}
        className={clsx('text-break-all', typographyProps?.className)}
      >
        {getDisplayValue(textFieldProps?.type, textFieldProps?.value)}
      </Typography>
    );
  }

  const handleChange = (value, validity) => {
    setState({ value, validity });
    setOpen(!validity);
  };

  const getInputPropValue = (
    key: string,
    properties: Array<MuiTextFieldProps>,
    defaultValue: any,
  ) => {
    let value = null;
    for (let i = 0; i < properties.length; i++) {
      try {
        value = properties[i][key];
        if (value == null) {
          value = properties[i]['inputProps'][key];
        }
        if (value != null) {
          break;
        }
      } catch (error) {}
    }
    return value != null ? value : defaultValue != null ? defaultValue : value;
  };

  const required = getInputPropValue('required', [textFieldProps]);

  const rawPlaceholder = getInputPropValue('placeholder', [textFieldProps], '');

  const placeholder = rawPlaceholder
    ? rawPlaceholder + (required ? T.translate('required') : '')
    : '';

  const textRenderer = (textProps: MuiTextFieldProps) => {
    return (
      <Tooltip
        title={getTooltipMessage(
          textFieldProps?.type || textProps?.type || 'text',
          {
            maxLength: getInputPropValue('maxLength', [
              textProps,
              textFieldProps,
            ]),
            min: getInputPropValue('min', [textProps, textFieldProps]),
            max: getInputPropValue('max', [textProps, textFieldProps]),
            minTime: getInputPropValue('minTime', [timeProps]),
            maxTime: getInputPropValue('maxTime', [timeProps]),
          },
        )}
        open={open}
        onClose={() => setOpen(false)}
        classes={{ tooltip: classes.tooltip }}
      >
        <TextField
          {...textFieldProps}
          {...textProps}
          className={clsx(textFieldProps?.className, textProps?.className)}
          inputProps={{
            ...textFieldProps?.inputProps,
            ...textProps?.inputProps,
            required,
            placeholder: placeholder || textProps?.inputProps?.placeholder,
            className: clsx(
              textFieldProps?.inputProps?.className,
              textProps?.inputProps?.className,
            ),
          }}
          onFocus={() => !validity && !open && setOpen(true)}
        />
      </Tooltip>
    );
  };

  if (textFieldProps?.type === 'time') {
    return (
      <LocalizationProvider dateAdapter={MomentUtils}>
        <DesktopTimePicker
          ampm={false}
          mask="__:__"
          inputFormat="HH:mm"
          disableOpenPicker
          size="small"
          variant="outlined"
          {...timeProps}
          value={value != null ? value : ''}
          onChange={(newValue) => {
            handleChange(newValue, !!newValue?.isValid?.());
            timeProps?.onChange?.(newValue);
          }}
          onError={(reason, value) => {
            handleChange(value, !reason);
            timeProps?.onError?.(reason, value);
          }}
          renderInput={(props) =>
            textRenderer({
              ...props,
              inputProps: {
                ...props.inputProps,
                size: value ? 5 : Math.max(5, getSize(placeholder)),
                maxLength: 5,
              },
              onKeyDown: (event) => {
                if (!/[0-9]/.test(event.key)) {
                  setOpen(true);
                }
              },
              helperText: undefined,
            })
          }
        />
      </LocalizationProvider>
    );
  }

  return textRenderer({
    value: value == null ? '' : value,
    error: !validity,
    inputProps: {
      ...([null, undefined, 'text'].includes(textFieldProps?.type) && {
        maxLength: 255,
        pattern: PRESENT_PATTERN,
        size: Math.max(
          value != null ? getSize(value.toString()) : 0,
          getSize(placeholder),
        ),
      }),
      ...(textFieldProps?.type === 'number' && {
        style: {
          width: `calc(1rem * ${
            (value > 999 ? value.toString() : '999').length
          })`,
        },
      }),
    },
    onChange: (event) => {
      const value = event.target.value;
      if (event.target.tagName === 'TEXTAREA') {
        // Textarea doesn't support pattern
        handleChange(value, validateString(value, required));
      } else {
        handleChange(value, event.target.checkValidity());
      }
      textFieldProps?.onChange?.(event);
    },
  });
};

export default Input;
